import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, ChevronRight, Crosshair, Loader2, ShieldCheck, Siren } from "lucide-react";
import { useAuth } from "../../auth/AuthProvider";
import { BookingApi, PaymentApi, PublicApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { location } from "../../platform";
import { Call911, StatusBadge } from "../../components/common";
import { ServiceNotice } from "./ServiceNotice";
import { dateTime } from "../../lib/format";

const ACTIVE = ["requested", "assigned", "accepted", "en_route", "at_pickup", "in_transit"];

function AvailabilityCheck() {
  const [state, setState] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try {
      const p = await location.getCurrentPosition();
      setState(await PublicApi.availability(p.lat, p.lng));
    } catch (e: any) {
      setState({ error: e.response ? errorMessage(e) : e.message });
    } finally { setBusy(false); }
  };
  return (
    <div className="fp-card space-y-3" data-testid="availability-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">Service availability</p>
          <p className="text-sm text-slate-600">Operating area: Metro Cebu</p>
        </div>
        <button className="fp-btn-secondary text-xs" onClick={check} disabled={busy} data-testid="availability-check-button">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />} Check my location
        </button>
      </div>
      {state?.error && <p className="text-sm text-amber-800" data-testid="availability-error">{state.error}</p>}
      {state && !state.error && <ServiceNotice data={state} />}
    </div>
  );
}

export default function PatientHome() {
  const { user } = useAuth();
  const { data: bookings } = useQuery({ queryKey: ["bookings"], queryFn: BookingApi.mine });
  const { data: membership } = useQuery({ queryKey: ["membership"], queryFn: PaymentApi.membership });
  const active = (bookings || []).filter((b) => ACTIVE.includes(b.status));

  return (
    <div className="space-y-5">
      <div>
        <p className="text-sm text-slate-500">Good day,</p>
        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl" data-testid="patient-greeting">{user.name}</h1>
      </div>

      {user.account_verified === false && (
        <Link to="/patient/verify" className="flex items-center gap-3 rounded-2xl border border-[#FFC400] bg-[#FFF4CC] p-4 text-[#003F32]" data-testid="verify-account-banner">
          <ShieldCheck size={24} className="shrink-0 text-[#036248]" />
          <span className="flex-1"><strong className="block">Verify your account</strong><span className="text-xs">Scan your package QR and card serial to activate your membership.</span></span>
          <ChevronRight size={18} />
        </Link>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Link to="/patient/book" data-testid="book-ptv-button" className="flex flex-col justify-between gap-2 rounded-3xl bg-gradient-to-br from-[#EEF7F3] to-[#FFF4CC] p-4 ring-1 ring-[#036248]/15 hover:ring-[#036248]">
          <img src="/ptv-van.png" alt="FastPass PTV van" className="h-16 w-auto self-start object-contain sm:h-20" />
          <span><span className="block font-display text-lg font-extrabold text-[#003F32]">Book a PTV</span><span className="text-xs text-[#036248]">Patient transport · add doctor or nurse</span></span>
        </Link>
        <Link to="/patient/emergency" data-testid="emergency-request-button" className="flex flex-col justify-between gap-2 rounded-3xl bg-[#FFC400] p-4 text-[#003F32] hover:bg-[#F4B400]">
          <Siren size={40} />
          <span><span className="block font-display text-lg font-extrabold">SOS Ambulance</span><span className="text-xs text-[#003F32]/80">Emergency pickup · nearest unit</span></span>
        </Link>
      </div>
      <Call911 compact />

      {active.map((b) => (
        <Link key={b.id} to={`/patient/bookings/${b.id}`} className="fp-card flex items-center justify-between gap-3 border-[#036248]" data-testid="active-booking-card">
          <div>
            <p className="fp-label">{b.type === "emergency" ? "Active emergency" : "Upcoming trip"} · {b.code}</p>
            <p className="mt-1 font-semibold text-slate-900">{b.status_label}</p>
            <p className="text-xs text-slate-500">{b.scheduled_at ? dateTime(b.scheduled_at) : dateTime(b.created_at)}</p>
          </div>
          <div className="flex items-center gap-2"><StatusBadge status={b.status} /><ChevronRight size={18} /></div>
        </Link>
      ))}

      <Link to="/patient/schedule" className="fp-card flex items-center gap-4 hover:border-[#036248]" data-testid="schedule-transport-button">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FBBF2E]/30 text-[#036248]"><CalendarClock size={24} /></span>
        <span className="flex-1">
          <span className="block font-semibold text-slate-900">Schedule medical transport</span>
          <span className="block text-sm text-slate-600">Check-ups, dialysis, discharge, transfers</span>
        </span>
        <ChevronRight size={18} />
      </Link>

      <AvailabilityCheck />

      <Link to="/patient/membership" className="fp-card flex items-center justify-between gap-3" data-testid="membership-summary-card">
        <div>
          <p className="fp-label">FASTPASS membership</p>
          <p className="mt-1 font-semibold text-slate-900">{membership?.active ? membership.active.plan_name : "Not a member yet"}</p>
          <p className="text-xs text-slate-500">{membership?.active ? `Valid until ${dateTime(membership.active.ends_at)}` : "Join for fare discounts and priority booking"}</p>
        </div>
        <ChevronRight size={18} />
      </Link>
    </div>
  );
}
